import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import styled from 'styled-components';

import BoundImageModal from '../../components/boundImageModal';
import {StyledPageCenterContainer, pxToEm} from '../../components/globalStyles';
import HexagonIconImage from '../../components/hexagonIconImage';
import Layout from '../../components/layout/layout';
import PageViewHeadline from '../../components/pageViewHeadline';
import BitmovinPlayer from '../../components/player/bitmovinPlayer';
import WhoIsUsingSection from '../../components/whoIsUsingSection';
import WhyBitmovinProductSection from '../../components/whyBitmovinProductSection';
import WrapperAppUl from '../../components/wrapperAppUl';
import {CompanyName} from '../../data/customerData';
import {Product, ProductToHexagonIconSrc, ProductToText} from '../../data/product';
import ModularAnalyticsSvg from '../../media/modularAnalytics.svg';
import CostaRicaPng from '../../media/playerPageView/costa-rica-poster.png';
import PlayerSpeedComparisonSvg from '../../media/playerPageView/player-speed-comparison.svg';

import NoteworthyFeaturesSection from './noteworthyFeaturesSection';
import PlayerAccordions from './playerAccordions';

const playerSource = {
  title: 'Costa Rica',
  dash: '//sales-demos-content.bitmovin.com/costa-rica-4k-60fps/stream_h264.mpd', // tslint:disable-line:max-line-length
  hls: '//sales-demos-content.bitmovin.com/costa-rica-4k-60fps/stream.m3u8', // tslint:disable-line:max-line-length
  poster: CostaRicaPng
};

//#region Styled Components

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledTwoColumnsSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > ${StyledContentColumn} {
    flex: 1 1 50%;

    :nth-child(1) {
      border-right: 2px solid ${baseVariables.colors.darkBlueGray};
      padding-right: 5%;
    }

    :nth-child(2) {
      padding-left: 5%;
    }
  }
`;

const StyledContentTwoColumnsSection = styled(StyledTwoColumnsSection)`
  padding-top: ${pxToEm(89)}em;
  padding-bottom: ${pxToEm(114)}em;
`;

const StyledListWrapperDiv = styled.div`
  width: 100%;
  padding: 0 ${pxToEm(20)}em;
  margin-bottom: ${pxToEm(30)}em;
`;

const StyledH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  color: ${baseVariables.colors.midnight};
  display: inline-block;
  margin: 0 0 ${pxToEm(55) / pxToEm(24)}em;
`;

const StyledPlayerAccordions = styled(PlayerAccordions)`
  margin-top: ${pxToEm(46)}em;
`;

const andMoreHeadlineFontSizePx = 22;
const StyledAndMoreHeadline = styled.div`
  font-size: ${pxToEm(andMoreHeadlineFontSizePx)}em;
  color: ${baseVariables.colors.midnight};
  margin-top: ${pxToEm(37) / pxToEm(andMoreHeadlineFontSizePx)}em;
  margin-bottom: ${pxToEm(32) / pxToEm(andMoreHeadlineFontSizePx)}em;
`;

const StyledNoteworthyFeaturesSection = styled(NoteworthyFeaturesSection)`
  margin-top: ${pxToEm(78)}em;
`;

const StyledPlayerWrapper = styled.div`
  width: 100%;
`;

const StyledMiddlePageCenterContainer = styled(StyledPageCenterContainer)`
  padding-top: 4rem;
  background-color: ${baseVariables.colors.lightBlueGray};
`;

const StyledImageModal = styled(BoundImageModal)`
  margin: 1em 0;
`;

//#endregion

const PlayerPageView: React.FunctionComponent = () => {
  const modularAnalytics = <img src={ModularAnalyticsSvg} alt="Analytics as Player Module" />;
  return (
    <Layout>
      <PageViewHeadline
        topSubHeadline={ProductToText[Product.Player]}
        headline={'Leading in quality playback'}
        badgeComponent={<HexagonIconImage iconSrc={ProductToHexagonIconSrc[Product.Player]} />}
      />

      <StyledPageCenterContainer>
        <StyledContentTwoColumnsSection>
          <StyledContentColumn>
            <StyledListWrapperDiv>
              <StyledH2>High Quality</StyledH2>
              <WrapperAppUl>
                <li>
                  Built to deliver a flawless viewing experience by supporting the most devices and platforms on the
                  market, helping you to <b>increase ad impressions</b>, <b>win new subscribers</b> and{' '}
                  <b>control costs</b>.{' '}
                </li>
                <li>
                  Automate your testing with <b>Bitmovin Stream Lab</b>. Streams are tested on actual devices in real
                  streaming environments.
                </li>
                <li>
                  Player is pre-integrated with Bitmovin’s Analytics solution. Take action fast by monitoring the viewer
                  experience with clear insights into error reporting and overall stream performance.
                </li>
              </WrapperAppUl>
            </StyledListWrapperDiv>
            <StyledListWrapperDiv>
              <StyledH2>Deploy everywhere</StyledH2>
              <WrapperAppUl>
                <li>
                  Officially supported on all major devices, and compatible with many more, so you can reach viewers on
                  every device.
                </li>
                <li>
                  Interactive wizards and unified developer experience make it fast to deploy Player. Pre-built
                  configurations optimize playback performance, even on the most demanding platforms.
                </li>
                <li>
                  Dedicated SSAI capability improves ad viewability, reduces rebuffering and improves content transition
                  times. Challenging platforms are fully supported, such as older Smart TV models.
                </li>
                <li>
                  Bitmovin’s patent-pending LL ABR is a proprietary algorithm that identifies the highest bitrate
                  available on each instance and adapts the stream to deliver a superior viewing experience with
                  broadcast-like latency.
                </li>
              </WrapperAppUl>
            </StyledListWrapperDiv>
            <StyledListWrapperDiv>
              <StyledH2>Ease of use</StyledH2>
              <WrapperAppUl>
                <li>
                  When integrating the Bitmovin Player, gain access to a unified developer experience, which reduces
                  time-to-market and total cost of ownership by over 25%.
                </li>
                <li>
                  With over 200 customizable features and partner integrations, such as pre-built ad capabilities and
                  DRM, we are constantly improving our player to facilitate any workflow, use case, and monetization
                  model.
                </li>
              </WrapperAppUl>
            </StyledListWrapperDiv>
            <BoundImageModal
              boundComponent={<Button variant={ButtonVariant.OutlinedLight}>Player Speed Comparison</Button>}
              source={PlayerSpeedComparisonSvg}
            />

            <StyledPlayerAccordions />
          </StyledContentColumn>

          <StyledContentColumn>
            <StyledPlayerWrapper>
              <BitmovinPlayer source={playerSource} />
            </StyledPlayerWrapper>
            <StyledNoteworthyFeaturesSection />
          </StyledContentColumn>
        </StyledContentTwoColumnsSection>
      </StyledPageCenterContainer>
      <StyledMiddlePageCenterContainer>
        <WhyBitmovinProductSection
          product={Product.Player}
          leftBulletPoints={[
            {
              headline: 'Plays everywhere',
              description: 'Bitmovin Player supports the largest number of platforms in the market today'
            },
            {
              headline: 'Built for speed',
              description: 'Fastest and lightest player in the market'
            },
            {
              headline: 'Always innovating',
              description:
                'Early and continuous R&D enables Bitmovin to provide the best video playback technologies, first!'
            }
          ]}
          rightBulletPoints={[
            {
              headline: 'Rich features & customizations',
              description:
                'We’ve solved the really complex video problems for Ads, DRM and 3rd party integrations for video playback' // tslint:disable-line:max-line-length
            },
            {
              headline: 'World class support',
              description:
                'We have a team of experts dedicated to supporting customers through integration, launch and special events' // tslint:disable-line:max-line-length
            }
          ]}
        />
      </StyledMiddlePageCenterContainer>
      <WhoIsUsingSection
        headline={'Who is using the Bitmovin Player?'}
        companies={[CompanyName.CBC, CompanyName.ClassPass, CompanyName.SlingTV, CompanyName.Periscope]}
      />
    </Layout>
  );
};

export default PlayerPageView;
